/**
 * TimelinePane — React renderer for TimelinePaneModel
 *
 * Lane-based operational replay timeline with Gantt-style temporal overlays
 * optimized for replay cognition, continuity awareness, admissibility visibility,
 * and cross-surface operational comprehension.
 *
 * REFINEMENTS (V1.2):
 * - Gantt-style lane-based timeline with shared time axis
 * - Point markers for events (duration not available in current model)
 * - Lane categorization: REFUSALS, BOUNDARIES, CONTINUITY, EXECUTIONS
 * - Temporal scanning and replay cognition
 * - Refusal clustering visibility
 * - SVG-based deterministic rendering
 */

import React, { useState, useMemo, useCallback } from 'react';
import type { TimelinePaneModel, TimelineRow } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { severityToColor, continuityToColor, sourceTagToChipColor, NEUTRAL, TYPOGRAPHY, ROW_HEIGHT, SPACING, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { selectionRowStyle } from '../motion/selectionMotionSemantics.js';
import { continuityPulseStyle, continuityDotAppearStyle } from '../motion/continuityMotionSemantics.js';

interface TimelinePaneProps {
  model: TimelinePaneModel;
  onSelectEvent?: (eventId: string) => void;
  replayWindow?: { start: string; end: string } | null;
}

type ViewMode = 'list' | 'gantt';

const LANE_HEIGHT = 32;
const LANE_CONFIG = [
  { id: 'refusals', label: 'REFUSALS', color: SEMANTIC.degraded },
  { id: 'boundaries', label: 'BOUNDARIES', color: SEMANTIC.unresolved },
  { id: 'continuity', label: 'CONTINUITY', color: SEMANTIC.stable },
  { id: 'executions', label: 'EXECUTIONS', color: SEMANTIC.stabilized },
] as const;

function getRowStyle(row: TimelineRow, inReplayWindow: boolean = false): React.CSSProperties {
  const color = row.continuityMarker
    ? continuityToColor(row.continuityMarker)
    : severityToColor(row.severity);

  const isAnomaly = row.isRefusal || row.isBoundaryViolation;
  const motionStyle = selectionRowStyle(row.isSelected, color.border);

  const base: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    height: ROW_HEIGHT.compact.timeline,
    paddingLeft: SPACING.sp3,
    paddingRight: SPACING.sp3,
    fontSize: TYPOGRAPHY.body.size,
    lineHeight: `${TYPOGRAPHY.body.lineHeight}px`,
    fontFamily: 'system-ui, sans-serif',
    color: isAnomaly ? SEMANTIC.degraded.text : NEUTRAL.text500,
    cursor: 'pointer',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    ...motionStyle,
  };

  if (isAnomaly) {
    base.backgroundColor = row.isSelected ? NEUTRAL.bg500 : 'rgba(18, 8, 8, 0.15)';
  }

  // Temporal focus window: dim events outside replay window
  if (!inReplayWindow) {
    base.opacity = 0.4;
    base.filter = 'grayscale(0.3)';
  } else {
    base.opacity = 1;
    base.filter = 'none';
  }

  if (row.continuityMarker) {
    const pulse = continuityPulseStyle(row.continuityMarker);
    base.boxShadow = pulse.boxShadow;
    base.transition = `${base.transition || ''}, ${pulse.transition || ''}`;
  }

  return base;
}

function getMarkerStyle(row: TimelineRow): React.CSSProperties {
  const color = row.continuityMarker
    ? continuityToColor(row.continuityMarker)
    : severityToColor(row.severity);

  return {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: color.marker,
    marginRight: SPACING.sp3,
    flexShrink: 0,
    border: row.isSelected ? `1px solid ${color.border}` : 'none',
    transition: 'border-color 160ms ease-out',
  };
}

function getContinuityDotStyle(marker: string): React.CSSProperties {
  const color = continuityToColor(marker as any);
  return {
    width: 4,
    height: 4,
    borderRadius: '50%',
    marginLeft: SPACING.sp2,
    flexShrink: 0,
    ...continuityDotAppearStyle(true, color.marker),
  };
}

function categorizeLane(row: TimelineRow): string {
  if (row.isRefusal) return 'refusals';
  if (row.isBoundaryViolation) return 'boundaries';
  if (row.continuityMarker) return 'continuity';
  return 'executions';
}

function formatCompactTime(timestamp: string): string {
  if (!timestamp) return '—';
  return timestamp.slice(11, 23);
}

export const TimelinePane: React.FC<TimelinePaneProps> = ({ model, onSelectEvent, replayWindow }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Helper to check if a timestamp is within the replay window
  const isInReplayWindow = useCallback((timestamp: string): boolean => {
    if (!replayWindow || !replayWindow.start || !replayWindow.end) return true;
    const ts = new Date(timestamp).getTime();
    const start = new Date(replayWindow.start).getTime();
    const end = new Date(replayWindow.end).getTime();
    return ts >= start && ts <= end;
  }, [replayWindow]);

  const ganttData = useMemo(() => {
    if (model.rows.length === 0) return null;

    const timestamps = model.rows.map(r => new Date(r.timestamp).getTime());
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    const timeRange = maxTime - minTime || 1;

    const lanes: Record<string, TimelineRow[]> = {
      refusals: [],
      boundaries: [],
      continuity: [],
      executions: [],
    };

    model.rows.forEach(row => {
      const lane = categorizeLane(row);
      lanes[lane].push(row);
    });

    return { lanes, minTime, maxTime, timeRange };
  }, [model.rows]);

  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Timeline error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        fontSize: TYPOGRAPHY.heading.size,
        fontWeight: TYPOGRAPHY.heading.weight,
        color: NEUTRAL.text600,
        backgroundColor: NEUTRAL.bg600,
        height: ROW_HEIGHT.compact.spanHeader,
      }}>
        <span>Timeline</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp3 }}>
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            {model.totalRows} events · {model.refusalCount} refusals
          </span>
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'gantt' : 'list')}
            style={{
              fontSize: TYPOGRAPHY.micro.size,
              padding: '2px 8px',
              backgroundColor: viewMode === 'gantt' ? NEUTRAL.bg500 : NEUTRAL.bg400,
              color: NEUTRAL.text300,
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            {viewMode === 'list' ? 'Gantt' : 'List'}
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {model.rows.map((row) => {
            const inWindow = isInReplayWindow(row.timestamp);
            const sourceChip = sourceTagToChipColor(row.sourceTag);
            return (
              <div
                key={row.eventId}
                style={getRowStyle(row, inWindow)}
                onClick={() => onSelectEvent?.(row.eventId)}
                title={`${row.eventType} · ${row.label}`}
                data-testid={`timeline-row-${row.eventId}`}
              >
                <div style={getMarkerStyle(row)} />
                <span style={{
                  fontFamily: TYPOGRAPHY.mono.family,
                  fontSize: TYPOGRAPHY.mono.size,
                  color: NEUTRAL.text300,
                  minWidth: 72,
                  marginRight: SPACING.sp3,
                  flexShrink: 0,
                }}>
                  {row.timestamp.slice(11, 23)}
                </span>
                <span style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  fontWeight: 600,
                  color: sourceChip.chipText,
                  backgroundColor: sourceChip.chip,
                  padding: '1px 5px',
                  borderRadius: 3,
                  minWidth: 70,
                  marginRight: SPACING.sp3,
                  flexShrink: 0,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                }}>
                  {row.sourceTag.slice(0, 12)}
                </span>
                <span style={{
                  color: row.isRefusal || row.isBoundaryViolation ? SEMANTIC.degraded.text : NEUTRAL.text500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                }}>
                  {row.label}
                </span>
                {row.continuityMarker && (
                  <div style={getContinuityDotStyle(row.continuityMarker)} title={`continuity: ${row.continuityMarker}`} />
                )}
                {row.proofOnly && (
                  <span style={{
                    marginLeft: SPACING.sp2,
                    fontSize: TYPOGRAPHY.micro.size,
                    color: NEUTRAL.text200,
                    padding: `1px 4px`,
                    borderRadius: 3,
                    backgroundColor: NEUTRAL.bg400,
                    flexShrink: 0,
                  }}>
                    restricted
                  </span>
                )}
              </div>
            );
          })}
          {model.rows.length === 0 && (
            <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' }}>
              No timeline events
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>
          {ganttData && ganttData.lanes.refusals.length + ganttData.lanes.boundaries.length + ganttData.lanes.continuity.length + ganttData.lanes.executions.length > 0 ? (
            <div style={{ position: 'relative', marginBottom: SPACING.sp4 }}>
              <svg
                width="100%"
                height={LANE_CONFIG.length * LANE_HEIGHT + 60}
                style={{ backgroundColor: NEUTRAL.bg200, borderRadius: 4 }}
              >
                {/* Lane backgrounds */}
                {LANE_CONFIG.map((lane, idx) => (
                  <rect
                    key={lane.id}
                    x={0}
                    y={idx * LANE_HEIGHT + 20}
                    width="100%"
                    height={LANE_HEIGHT}
                    fill={lane.color.chip}
                    fillOpacity={0.1}
                    stroke={lane.color.border}
                    strokeWidth={0.5}
                    strokeOpacity={0.3}
                  />
                ))}

                {/* Lane labels */}
                {LANE_CONFIG.map((lane, idx) => (
                  <text
                    key={lane.id}
                    x={10}
                    y={idx * LANE_HEIGHT + 20 + LANE_HEIGHT / 2}
                    fontSize={TYPOGRAPHY.micro.size}
                    fill={lane.color.text}
                    dominantBaseline="middle"
                  >
                    {lane.label}
                  </text>
                ))}

                {/* Event points */}
                {LANE_CONFIG.map((lane, laneIdx) => {
                  const laneRows = ganttData!.lanes[lane.id];
                  return laneRows.map(row => {
                    const time = new Date(row.timestamp).getTime();
                    const x = ((time - ganttData!.minTime) / ganttData!.timeRange) * 100;
                    const color = row.continuityMarker
                      ? continuityToColor(row.continuityMarker)
                      : severityToColor(row.severity);
                    return (
                      <circle
                        key={row.eventId}
                        cx={`${x}%`}
                        cy={laneIdx * LANE_HEIGHT + 20 + LANE_HEIGHT / 2}
                        r={5}
                        fill={color.chip}
                        stroke={color.border}
                        strokeWidth={2}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onSelectEvent?.(row.eventId)}
                      />
                    );
                  });
                })}

                {/* Refusal cluster markers */}
                {ganttData.lanes.refusals.length > 1 && ganttData.lanes.refusals.map(row => {
                  const time = new Date(row.timestamp).getTime();
                  const x = ((time - ganttData.minTime) / ganttData.timeRange) * 100;
                  return (
                    <circle
                      key={`refusal-cluster-${row.eventId}`}
                      cx={`${x}%`}
                      cy={20 + LANE_HEIGHT / 2}
                      r={8}
                      fill="none"
                      stroke={SEMANTIC.degraded.marker}
                      strokeWidth={2}
                      strokeDasharray="2,2"
                    />
                  );
                })}

                {/* Time axis labels */}
                <text
                  x="0%"
                  y={LANE_CONFIG.length * LANE_HEIGHT + 40}
                  fontSize={TYPOGRAPHY.micro.size}
                  fill={NEUTRAL.text400}
                  textAnchor="start"
                >
                  {formatCompactTime(model.earliestTimestamp || '')}
                </text>
                <text
                  x="100%"
                  y={LANE_CONFIG.length * LANE_HEIGHT + 40}
                  fontSize={TYPOGRAPHY.micro.size}
                  fill={NEUTRAL.text400}
                  textAnchor="end"
                >
                  {formatCompactTime(model.latestTimestamp || '')}
                </text>
              </svg>
            </div>
          ) : (
            <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' }}>
              No timeline events
            </div>
          )}
        </div>
      )}
    </div>
  );
};
